import { Popover, theme, Options, Option, Text } from '@alefeducation/ui-elements-library-core'
import { Check, CaretDownSolid, CaretUpSolid } from '@alefeducation/ui-elements-library-icons'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useState } from 'react'
import styled from 'styled-components'
import { CONTENT_TYPES_LIST, CONTENT_TYPE_KEYS_ARRAY, CONTENT_TYPE_KEYS } from '../../utils/constants'

const DropDownArrow = ({ isOpen }: { isOpen: boolean }) => {
  const styles = {
    width: '12px',
    color: theme.colors.primary['100']
  }
  return isOpen ? <CaretUpSolid {...styles} /> : <CaretDownSolid {...styles} />
}

export type ContentTypeFiltersProps = {
  contentTypes: CONTENT_TYPE_KEYS_ARRAY
  onContentTypeChange: (selectedFilters: CONTENT_TYPE_KEYS_ARRAY) => void
  multiSelect?: boolean
}

const ContentTypeFilterWrapper = styled.div`
  border: 1px solid ${theme.colors.primary['40']};
  background-color: ${theme.colors.white};
  display: flex;
  border-radius: 0px 8px 8px 0px;
  .filter-head {
    display: flex;
    padding: 0 16px;
    width: 100%;
    align-items: center;
    justify-content: space-between;
  }
  #content-filter-dropdown {
    width: 272px;
    > div.options {
      max-height: 100vh;
      height: auto;
    }
    .option-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      text-transform: capitalize;
    }
    ul > li > div > div {
      display: block;
    }
  }
`

const ContentTypesFilter = (props: ContentTypeFiltersProps) => {
  const { contentTypes, onContentTypeChange, multiSelect = true } = props
  const [popoverRef, setPopoverRef] = useState<null | HTMLDivElement>(null)
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false)
  const { allowedTypesAuditLogs } = useFlags()
  const selectedTypesSelection = (selected: CONTENT_TYPE_KEYS) => {
    if (!multiSelect) {
      if (selected !== contentTypes[0]) {
        onContentTypeChange([selected])
      }
      setIsFilterOpen(false)
      return
    }
    let types = [...contentTypes]
    if (types.includes(selected)) {
      types = [...types.filter(f => f !== selected)]
    } else {
      types = [...types, selected]
    }
    onContentTypeChange(types)
  }

  const contentList: ContentTypeFiltersProps['contentTypes'] = Object.keys(CONTENT_TYPES_LIST).filter(f =>
    (allowedTypesAuditLogs ?? '').split(',').includes(f.toLowerCase())
  ) as CONTENT_TYPE_KEYS_ARRAY
  return (
    <ContentTypeFilterWrapper className="filters-dropdown" data-testid="content-type-filter">
      <div
        className="filter-head"
        data-testid="content-filter-head"
        ref={setPopoverRef}
        role="button"
        tabIndex={1}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            setIsFilterOpen(!isFilterOpen)
          }
        }}
        onClick={() => setIsFilterOpen(!isFilterOpen)}
      >
        <Text variant="body-large" weight="light">
          {`in ${contentTypes.map(m => CONTENT_TYPES_LIST[m]).join(', ')}`}
        </Text>
        <DropDownArrow isOpen={isFilterOpen} />
      </div>
      {popoverRef ? (
        <Popover
          data-testid="content-filter-options"
          id="content-filter-dropdown"
          anchorRef={popoverRef}
          isOpen={isFilterOpen}
          popoverPosition="bottom"
          setIsOpen={setIsFilterOpen}
        >
          <Options>
            {contentList.map(type => (
              <Option
                key={type}
                onClick={label => selectedTypesSelection(label as CONTENT_TYPE_KEYS)}
                label={type}
                data-testid={`content-option-${type}`}
              >
                <div className="option-content">
                  <span>{CONTENT_TYPES_LIST[type]}</span>
                  {contentTypes.includes(type as CONTENT_TYPE_KEYS) ? (
                    <Check className="check-tick" color={theme.colors.success['100']} />
                  ) : null}
                </div>
              </Option>
            ))}
          </Options>
        </Popover>
      ) : null}
    </ContentTypeFilterWrapper>
  )
}

export default ContentTypesFilter
